import { X } from "lucide-react";
import { type HTMLAttributes, type JSX, type ReactNode, type Ref, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "@/lib/use-focus-trap";
import { cn } from "@/lib/utils";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  ariaLabel: string;
}

export function Sheet({ open, onOpenChange, children, ariaLabel }: SheetProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);

  useFocusTrap(ref, open);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return <></>;

  return createPortal(
    <div
      ref={ref}
      tabIndex={-1}
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative showModal/close calls
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 flex"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Esc handler is registered above; the backdrop must stay non-focusable so the focus trap lands on the sheet's content (see tests/components/use-focus-trap.test.tsx) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/40"
        onClick={() => onOpenChange(false)}
      />
      {children}
    </div>,
    document.body,
  );
}

interface SheetContentProps extends HTMLAttributes<HTMLDivElement> {
  side?: "right" | "left";
  onClose?: () => void;
  ref?: Ref<HTMLDivElement>;
}

export function SheetContent({
  className,
  side = "right",
  onClose,
  children,
  ref,
  ...props
}: SheetContentProps) {
  return (
    <div
      ref={ref}
      className={cn(
        "relative z-10 flex h-full w-full flex-col overflow-y-auto border-border bg-card text-card-foreground shadow-lg",
        side === "right" ? "ml-auto border-l" : "mr-auto border-r",
        className,
      )}
      {...props}
    >
      {children}
      {onClose ? (
        <button
          type="button"
          aria-label="Cerrar"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  );
}

export function SheetHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col space-y-2 border-b border-border px-4 py-3 pr-12", className)}
      {...props}
    />
  );
}

export function SheetTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-heading-3 font-semibold", className)} {...props} />;
}

export function SheetDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-body-3 text-muted-foreground", className)} {...props} />;
}

export function SheetBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex-1 space-y-4 overflow-y-auto px-4 py-4", className)} {...props} />;
}

export function SheetFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-3",
        className,
      )}
      {...props}
    />
  );
}
